import React from 'react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { TraceItem } from '@/types/trace';
import { formatDuration, formatTokens } from '@/utils/traceTransformer';

interface MetadataTabProps {
  item: TraceItem;
}

interface MetadataRowProps {
  label: string;
  value: React.ReactNode;
}

function MetadataRow({ label, value }: MetadataRowProps) {
  return (
    <div className="flex items-start justify-between py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-mono text-right max-w-[60%] break-all">
        {value}
      </span>
    </div>
  );
}

// Format date for display
function formatDateTime(timestamp: number | string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export function MetadataTab({ item }: MetadataTabProps) {
  return (
    <ScrollArea className="h-full pr-4">
      <div className="space-y-4 pb-4">
        {/* Basic Info */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Basic Info
          </h4>
          <div className="space-y-1 divide-y">
            <MetadataRow label="ID" value={item.id} />
            <MetadataRow label="Type" value={
              <Badge variant="outline">{item.type}</Badge>
            } />
            <MetadataRow label="Status" value={
              <Badge variant={item.status === 'success' ? 'default' : 'destructive'}>
                {item.status}
              </Badge>
            } />
            <MetadataRow label="Depth" value={item.depth} />
          </div>
        </div>

        <Separator />

        {/* Timing */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Timing
          </h4>
          <div className="space-y-1 divide-y">
            <MetadataRow label="Duration" value={formatDuration(item.duration)} />
            {item.startTime && (
              <MetadataRow
                label="Start Time"
                value={formatDateTime(item.startTime)}
              />
            )}
            {item.endTime && (
              <MetadataRow
                label="End Time"
                value={formatDateTime(item.endTime)}
              />
            )}
          </div>
        </div>

        <Separator />

        {/* Tokens */}
        <div>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Token Usage
          </h4>
          <div className="space-y-1 divide-y">
            <MetadataRow label="Input Tokens" value={formatTokens(item.tokenCount.input)} />
            <MetadataRow label="Output Tokens" value={formatTokens(item.tokenCount.output)} />
            <MetadataRow label="Total Tokens" value={formatTokens(item.tokenCount.total)} />
            {item.cost !== null && (
              <MetadataRow label="Cost" value={`$${item.cost.toFixed(4)}`} />
            )}
          </div>
        </div>

        {/* Task-specific */}
        {item.taskId && (
          <>
            <Separator />
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Task Info
              </h4>
              <div className="space-y-1 divide-y">
                <MetadataRow label="Task ID" value={item.taskId} />
                {item.taskNumber && (
                  <MetadataRow label="Task Number" value={item.taskNumber} />
                )}
                {item.taskType && (
                  <MetadataRow label="Task Type" value={item.taskType} />
                )}
                {item.worker && (
                  <MetadataRow label="Worker" value={item.worker} />
                )}
              </div>
            </div>
          </>
        )}

        {/* Tool-specific */}
        {item.toolName && (
          <>
            <Separator />
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Tool Info
              </h4>
              <div className="space-y-1 divide-y">
                <MetadataRow label="Tool Name" value={item.toolName} />
                <MetadataRow label="Cache Hit" value={item.isCacheHit ? 'Yes' : 'No'} />
              </div>
            </div>
          </>
        )}

        {/* Custom Metadata */}
        {Object.keys(item.metadata).length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Custom Metadata
              </h4>
              <div className="space-y-1 divide-y">
                {Object.entries(item.metadata).map(([key, value]) => (
                  <MetadataRow
                    key={key}
                    label={key}
                    value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </ScrollArea>
  );
}

export default MetadataTab;
